/**
 * Mirrors `backend/src/government/government.types.ts`.
 *
 * The country the directory covers, the state structure the AI researches
 * and people confirm, the runs that do the research, and the updates to
 * office holders it proposes. Keep this file in step with the backend one —
 * nothing here is frontend-only.
 */


/* ------------------------------------------------------------------ */
/* Country                                                             */
/* ------------------------------------------------------------------ */

/** How often the appointments sweep runs on its own */
export type AutoCheck = "off" | "weekly" | "daily";

export interface CountryProfile {
  /** ISO 3166-1 alpha-2, e.g. "GN" */
  code: string;
  /** English short name, e.g. "Guinea" */
  name: string;
  /** Languages research is done in, official first, e.g. ["fr", "en"] */
  languages: string[];
  /** Which language an entity's name is written in on its record */
  nameLanguage: string;
  autoCheck: AutoCheck;
  updatedAt: string | null;
}

export interface CountryInput {
  code: string;
  languages?: string[];
  nameLanguage?: string;
  autoCheck?: AutoCheck;
}

/* ------------------------------------------------------------------ */
/* Structure                                                           */
/* ------------------------------------------------------------------ */

export type GovNodeKind =
  | "head_of_state"
  | "head_of_government"
  | "ministry"
  | "directorate"
  | "agency"
  | "state_enterprise"
  | "legislature"
  | "judiciary"
  | "independent_authority"
  | "local_government"
  | "security_force"
  | "diplomatic_mission"
  | "other";

export const GOV_NODE_KINDS: GovNodeKind[] = [
  "head_of_state",
  "head_of_government",
  "ministry",
  "directorate",
  "agency",
  "state_enterprise",
  "legislature",
  "judiciary",
  "independent_authority",
  "local_government",
  "security_force",
  "diplomatic_mission",
  "other",
];

/**
 * proposed: found by research, nobody has looked yet
 * confirmed: a person agreed it belongs in the structure, as placed
 * rejected: a person said it does not
 * applied: written to the directory (an organization record exists for it)
 */
export type GovNodeStatus = "proposed" | "confirmed" | "rejected" | "applied";

export type SourceState = "live" | "dead" | "unknown";

/**
 * What a source is. The state is described by its own acts — decrees,
 * orders, decisions, the official journal — and by its institutions' own
 * sites and documents; those are what the map rests on. News reports them,
 * and confirms them, but is not the record.
 */
export type SourceKind =
  | "decree"
  | "order"
  | "decision"
  | "gazette"
  | "official_site"
  | "official_document"
  | "news"
  | "other";

export const OFFICIAL_SOURCE_KINDS: SourceKind[] = [
  "decree",
  "order",
  "decision",
  "gazette",
  "official_site",
  "official_document",
];

export interface GovSource {
  url: string;
  title?: string | null;
  publisher?: string | null;
  /** YYYY, YYYY-MM or YYYY-MM-DD */
  date?: string | null;
  /** What this source says, in one sentence */
  claim?: string | null;
  status?: SourceState;
  kind?: SourceKind;
}

export interface GovHead {
  name: string;
  title?: string | null;
  /** When they took up the post, in this unbroken tenure */
  since?: string | null;
  /**
   * The latest act that only kept them in it — a new government that keeps
   * a minister, a decree confirming a director. It does not restart the
   * tenure, so it is never read as "since".
   */
  confirmedOn?: string | null;
  /** The directory person this head was matched to, when there is one */
  stakeholderId?: string | null;
}

export interface FormerName {
  name: string;
  /** When the entity stopped using this name */
  until?: string | null;
  source?: string | null;
}

/** How a directory record relates to a node of the structure */
export type MatchRelation =
  | "same"
  | "translation"
  | "former_name"
  | "acronym"
  /** The record names a post of the body ("Directeur de Cabinet de …"), not a body */
  | "post"
  | "possible";

export interface GovMatch {
  organizationId: string;
  label: string;
  type: string | null;
  parentLabel: string | null;
  /** People attached to this record (roles) */
  people: number;
  /** 0–100: how sure we are this record is this entity */
  score: number;
  relation: MatchRelation;
  reason: string;
  /** curated: the same body, found twice in the map and folded together */
  via: "ai" | "name" | "both" | "manual" | "curated";
}

export type GovDecisionAction = "create" | "link" | "merge" | "skip";

export interface GovDecision {
  action: GovDecisionAction;
  /** The existing record that stays (link or merge) */
  survivorId?: string | null;
  /** Other existing records folded into the survivor (merge) */
  mergeIds?: string[];
  /** True while the system chose it; false once a person changed it */
  auto?: boolean;
}

export interface LinkedOrganization {
  id: string;
  label: string;
  type: string | null;
  /** The record this one is filed under in the directory, which is what the
   * graph draws: the directory is where an entity actually sits */
  parentId: string | null;
  parentLabel: string | null;
  people: number;
}

export interface GovNodeView {
  id: string;
  parentId: string | null;
  kind: GovNodeKind;
  name: string;
  nameEn: string | null;
  acronym: string | null;
  aliases: string[];
  description: string | null;
  website: string | null;
  head: GovHead | null;
  formerNames: FormerName[];
  sources: GovSource[];
  confidence: number;
  status: GovNodeStatus;
  /** Not found again by the latest full research */
  stale: boolean;
  /** It no longer exists — split, merged or replaced — and a person agreed */
  ended: NodeEnding | null;
  /** Other wordings research has used for this same body, merged into it */
  variants: string[];
  /** A person edited the name or placement; research no longer overwrites it */
  edited: boolean;
  organizationId: string | null;
  organization: LinkedOrganization | null;
  matches: GovMatch[];
  decision: GovDecision;
  researchedAt: string | null;
  appliedAt: string | null;
  order: number;
}

/**
 * How bodies of the state change shape.
 *
 * split: one body became several; merge: several became one; replaced: one
 * body took over from another under a different identity; dissolved: it went
 * and nothing took over. A plain rename is not here — the body is the same,
 * only its name changed, and that is an update of kind "rename".
 */
export type ReorganisationKind = "split" | "merge" | "replaced" | "dissolved";

/** One side of a reorganisation: a mapped entity, its record, or both */
export interface OrgRef {
  nodeId: string | null;
  organizationId: string | null;
  label: string;
}

export interface Reorganisation {
  how: ReorganisationKind;
  from: OrgRef[];
  into: OrgRef[];
  /** The act that did it, as research cites it */
  act: string | null;
}

/** How an entity came to an end, once a person agreed that it did */
export interface NodeEnding {
  how: ReorganisationKind;
  on: string | null;
  into: OrgRef[];
  /** The reviewed change that ended it */
  updateId: string | null;
}

/** What a gov-looking directory record outside the structure probably is */
export type OutsideVerdict =
  | "foreign_government"
  | "not_public"
  | "public_unplaced"
  | "obsolete";

export interface OutsideRecord {
  organizationId: string;
  label: string;
  type: string | null;
  parentLabel: string | null;
  people: number;
  verdict: OutsideVerdict;
  suggestion: "detach" | "move" | "keep";
  /** For "move": the node it belongs under */
  targetNodeId: string | null;
  reason: string;
  confidence: number;
}

export interface StructureStats {
  total: number;
  proposed: number;
  confirmed: number;
  applied: number;
  rejected: number;
  stale: number;
  /** Confirmed or proposed nodes that would fold several records into one */
  toMerge: number;
  /** Confirmed or proposed nodes with no record yet */
  toCreate: number;
  /** Nodes tied to at least one existing record */
  matched: number;
  /** Confirmed and not applied yet */
  ready: number;
}

export interface StructureView {
  country: CountryProfile;
  nodes: GovNodeView[];
  outside: OutsideRecord[];
  stats: StructureStats;
  matchedAt: string | null;
  activeRun: RunView | null;
}

export interface NodePatch {
  name?: string;
  nameEn?: string | null;
  acronym?: string | null;
  kind?: GovNodeKind;
  parentId?: string | null;
  website?: string | null;
  description?: string | null;
  status?: "proposed" | "confirmed" | "rejected";
  decision?: GovDecision;
  /** null: it still exists — a reviewed change marked it ended by mistake */
  ended?: null;
}

export interface NewNodeInput {
  name: string;
  nameEn?: string | null;
  acronym?: string | null;
  kind: GovNodeKind;
  parentId?: string | null;
}

export interface ApplyResult {
  nodeId: string;
  ok: boolean;
  organizationId?: string;
  /** The name the record carries now */
  label?: string;
  created?: boolean;
  /** Its record had been deleted from the directory, so it was made again */
  recreated?: boolean;
  merged?: number;
  renamed?: "official" | "correction" | null;
  movedPeople?: number;
  /** Duplicate roles — one person, one post, recorded twice — collapsed */
  collapsedRoles?: number;
  /** What happened to the head research found, when it was asked for */
  head?: HeadOutcome | null;
  error?: string;
}

/** Recording the head research found at an entity, and what that did */
export interface HeadOutcome {
  ok: boolean;
  /** The change it went through, now in the Updates history */
  updateId?: string | null;
  kind?: "appointment" | "correction" | "recorded";
  stakeholderId?: string | null;
  personLabel?: string;
  createdPerson?: boolean;
  role?: string | null;
  since?: string | null;
  closed?: ClosedRole[];
  error?: string;
}

/** A role that applying a change ended, and the day it ended */
export interface ClosedRole {
  assignmentId: string;
  stakeholderId: string;
  personLabel: string;
  role: string;
  endDate: string;
}

/**
 * Everything applying one entity would do to the directory, worked out by the
 * server before anything is written — so the person agreeing sees the
 * records made, renamed and moved, and the roles begun and ended.
 */
export interface NodePlan {
  nodeId: string;
  /** The name the record will carry */
  name: string;
  /** Why it cannot be applied as it stands, when it cannot */
  blocked: string | null;
  record: {
    /** create: new record; recreate: its record was deleted and is made again */
    action: "create" | "recreate" | "keep" | "skip";
    id: string | null;
    label: string | null;
  };
  merges: Array<{ id: string; label: string; people: number }>;
  rename: { from: string; to: string; kind: "official" | "correction" } | null;
  parent: { from: string | null; to: string | null } | null;
  type: { from: string | null; to: string } | null;
  /** Parents with no record yet, which are applied first */
  parentsFirst: string[];
  head: HeadPlan | null;
  /** People recorded twice in one post across these records, kept once */
  duplicateRoles: Array<{
    personLabel: string;
    post: string;
    titles: string[];
  }>;
}

export interface HeadPlan {
  name: string;
  title: string;
  since: string | null;
  person: PersonRef;
  /** appoint: a role is created; correct: the recorded start is wrong; recorded: nothing to do */
  action: "appoint" | "correct" | "recorded";
  /** Roles that end when the head is recorded */
  closes: RoleRef[];
  /** The day those roles end */
  endsOn: string | null;
  correction: FieldChange | null;
  /** A change waiting in the queue that says the same thing */
  pendingUpdateId: string | null;
  /** How far the sources behind the head go */
  sources: number;
}

export interface HeadChoice {
  include: boolean;
  stakeholderId?: string | null;
  createPerson?: boolean;
  closeAssignmentIds?: string[];
}

export interface ApplyNodesInput {
  ids: string[];
  /** Per entity: whether to record the head research found, and how */
  heads?: Record<string, HeadChoice>;
}

/** Changes whenever anything the government map shows has changed */
export interface ChangeStamp {
  stamp: string;
}

/* ------------------------------------------------------------------ */
/* Research runs                                                       */
/* ------------------------------------------------------------------ */

export type RunKind =
  /** The whole structure, from the top down */
  | "structure"
  /** One node's sub-structure, deeper */
  | "branch"
  /** Matching the structure to the directory (no web search) */
  | "match"
  /** Who holds the key posts at chosen entities, compared with the directory */
  | "officials"
  /** Appointments, dismissals and reassignments announced recently */
  | "appointments"
  /** A second, focused check of one proposed update */
  | "verify"
  /**
   * Find duplicates: the entries of the map, and the records tied to them,
   * that are one body — assessed by a model, checked on the web when it is
   * unsure, and proposed for merging
   */
  | "curate"
  /**
   * The government's history: the acts that formed and reshaped it, read one
   * by one, and who held each post from when to when
   */
  | "timeline";

export type RunStatus =
  /** Accepted, waiting for the run ahead of it to finish */
  "queued" | "running" | "done" | "failed" | "cancelled" | "interrupted";

export type LaneStatus = "queued" | "running" | "done" | "failed" | "skipped";

/** One parallel strand of a run: a ministry being researched, a batch checked */
export interface RunLane {
  key: string;
  label: string;
  status: LaneStatus;
  detail?: string | null;
  count?: number | null;
}

export interface RunLogEntry {
  at: string;
  message: string;
  level: "info" | "step" | "warn" | "error" | "success";
  lane?: string | null;
}

export interface RunScope {
  depth?: "core" | "full";
  /** The interrupted run this one continues, so finished work is not redone */
  resumeOf?: string | null;
  nodeId?: string | null;
  organizationIds?: string[];
  windowDays?: number;
  updateId?: string | null;
  focus?: string | null;
  /** For a timeline: how far back to read the acts (YYYY-MM-DD) */
  since?: string | null;
}

export interface RunView {
  id: string;
  kind: RunKind;
  status: RunStatus;
  country: string;
  title: string;
  scope: RunScope;
  startedAt: string;
  finishedAt: string | null;
  createdBy: string | null;
  progress: { done: number; total: number };
  lanes: RunLane[];
  log: RunLogEntry[];
  /** Total entries, so a poller can ask only for what it has not seen */
  logSize: number;
  stats: Record<string, number>;
  error: string | null;
  providers: string[];
  /** 1 for next up, while queued; null once it is running or over */
  queuePosition: number | null;
}

export interface StartRunInput extends RunScope {
  kind: RunKind;
}

/* ------------------------------------------------------------------ */
/* Updates to office holders and names                                 */
/* ------------------------------------------------------------------ */

/**
 * appointment: someone takes a post (and whoever held it leaves it)
 * departure: someone leaves a post
 * correction: a recorded role is wrong (title, start date)
 * rename: an entity officially changed its name
 * restructure: entities were split, merged or replaced by others
 * duplicate: entries of the map are one body (or a post of one), to merge
 */
export type UpdateKind =
  | "appointment"
  | "departure"
  | "correction"
  | "rename"
  | "restructure"
  | "duplicate"
  /** A body filed under another branch of the state */
  | "move"
  /** Someone the acts show in a post before: recorded as a former role */
  | "former";
export type UpdateStatus = "pending" | "applied" | "dismissed" | "superseded";

/**
 * corroborated: two or more independent live sources
 * single_source: one live source
 * unverified: no source could be reached
 * conflicting: sources disagree
 * refuted: a focused check found the opposite
 */
export type Verification =
  | "corroborated"
  | "single_source"
  | "unverified"
  | "conflicting"
  | "refuted";

export interface RoleRef {
  assignmentId: string;
  stakeholderId: string;
  personLabel: string;
  role: string;
  organizationId: string;
  organizationLabel: string;
  startDate: string | null;
}

export interface PersonRef {
  id: string | null;
  label: string;
  /** Not in the directory: applying creates the person */
  isNew: boolean;
  /** Other people the name could belong to */
  candidates: Array<{ id: string; label: string; score: number }>;
}

export interface FieldChange {
  field: string;
  from: string | null;
  to: string | null;
}

/**
 * A decree that kept someone in a post they already held — "reconduit",
 * "maintenu", "confirmé dans ses fonctions", a new government keeping its
 * minister. It says they are in post; it is not when they took it.
 */
export interface Confirmation {
  date: string | null;
  act: string | null;
}

/** One entry of the map a duplicate proposal is about, as it stood then */
export interface DuplicateEntry {
  nodeId: string;
  name: string;
  /** The entry it sits under in the map */
  parent: string | null;
  organizationId: string | null;
  /** Its record's name, when it has one */
  record: string | null;
  /** Current roles at its record */
  people: number;
  /** A post listed as if it were a body */
  post?: boolean;
}

/** Entries that are one body: the one kept, and the ones folded into it */
export interface DuplicateProposal {
  keep: DuplicateEntry;
  fold: DuplicateEntry[];
  /** The official name, when the check found one no entry carries */
  name: string | null;
  /** Who heads the body today, when the check found it */
  head: string | null;
}

export interface GovUpdateView {
  id: string;
  kind: UpdateKind;
  status: UpdateStatus;
  organization: {
    id: string | null;
    label: string;
    /** The body research placed it under, when it is not in the directory */
    parent: string | null;
  };
  person: PersonRef | null;
  /** Canonical post key, e.g. "minister", "secretary_general" */
  post: string | null;
  roleTitle: string | null;
  effectiveDate: string | null;
  /** Roles that end when this is applied: the predecessor, or the person's own */
  closes: RoleRef[];
  /** The role a correction changes */
  target: RoleRef | null;
  changes: FieldChange[];
  /** What a restructure splits, merges or replaces */
  reorganisation: Reorganisation | null;
  /** For a duplicate: the entries that are one body */
  duplicate: DuplicateProposal | null;
  /** For an appointment the act only confirmed: that act, which is not the start */
  confirmation: Confirmation | null;
  summary: string;
  confidence: number;
  verification: Verification;
  evidence: GovSource[];
  provider: string | null;
  model: string | null;
  runId: string | null;
  createdAt: string;
  reviewedAt: string | null;
  dismissReason: string | null;
  applied: Record<string, unknown> | null;
}

export interface ApplyUpdateInput {
  /** A different directory person than the one matched */
  stakeholderId?: string | null;
  /** Create the person instead of using a match */
  createPerson?: boolean;
  organizationId?: string | null;
  /** Create the organization research named, which the directory lacks */
  createOrganization?: boolean;

  roleTitle?: string | null;
  effectiveDate?: string | null;
  /** Which of the proposed roles to close; all of them when absent */
  closeAssignmentIds?: string[];
  /** For a duplicate: the entry to keep, when not the one proposed */
  keepNodeId?: string | null;
}

export interface UpdateQuery {
  status?: UpdateStatus;
  kind?: UpdateKind;
  organizationId?: string;
  stakeholderId?: string;
  limit?: number;
}

export interface UpdateCounts {
  pending: number;
  byKind: Record<UpdateKind, number>;
}

/* ------------------------------------------------------------------ */
/* Who holds the posts                                                 */
/* ------------------------------------------------------------------ */

/**
 * One current role at an entity of the state, as the directory holds it.
 *
 * The chart of the state is drawn from these: a ministry with a minister
 * recorded reads differently from one with nobody, and a head research found
 * but the directory has never heard of is a gap somebody can close.
 */
export interface GovOfficeHolder {
  organizationId: string;
  stakeholderId: string;
  name: string;
  /** The role as the record words it */
  role: string;
  /** The canonical post, e.g. "minister"; "other" when it is not one we name */
  post: string;
  /** How senior the post is, lower first — the order they arrive in */
  rank: number;
  since: string | null;
  imageId: string | null;
}

/* ------------------------------------------------------------------ */
/* Name history                                                        */
/* ------------------------------------------------------------------ */

/**
 * official: the entity was renamed (decree, restructuring)
 * correction: the record's spelling or form was fixed
 * merge: another record's name was folded in
 */
export type NameChangeKind = "official" | "correction" | "merge";

export interface NameChangeView {
  id: string;
  organizationId: string;
  previousName: string;
  newName: string;
  previousAcronym: string | null;
  newAcronym: string | null;
  kind: NameChangeKind;
  effectiveDate: string | null;
  source: string | null;
  sourceUrl: string | null;
  note: string | null;
  origin: "manual" | "edit" | "government";
  recordedBy: string | null;
  recordedByName: string | null;
  recordedAt: string;
}

export interface RenameInput {
  name: string;
  acronym?: string | null;
  kind: "official" | "correction";
  effectiveDate?: string | null;
  source?: string | null;
  sourceUrl?: string | null;
  note?: string | null;
}

/* ------------------------------------------------------------------ */
/* The timeline: acts, and who held what when                          */
/* ------------------------------------------------------------------ */

export type ActKind = "government" | "appointments" | "appointment" | "dismissal" | "other";

export type ActEffect = "appointed" | "confirmed" | "moved" | "removed" | "listed";

export interface ActMember {
  name: string;
  /** The title exactly as the act writes it */
  title: string;
  body: string | null;
  acting?: boolean;
  effect?: ActEffect;
}

export type ActStatus = "found" | "read" | "failed";

export interface GovActView {
  id: string;
  date: string;
  kind: ActKind;
  reference: string | null;
  title: string;
  /** Lists every member of the government in office after it */
  complete: boolean;
  /** Ends the functions of the whole government at once */
  endsAll?: boolean;
  members: ActMember[];
  sources: GovSource[];
  confidence: number;
  status: ActStatus;
}

export interface ActRefView {
  id: string;
  date: string;
  reference: string | null;
}

/** One person's unbroken time in one post, as the acts tell it */
export interface TenureView {
  person: string;
  /** The same for every spelling of one person: "Dr Bernard Goumou", "Bernard GOUMOU" */
  personKey?: string;
  post: string;
  title: string;
  body: string | null;
  from: string;
  fromAct: ActRefView;
  to: string | null;
  toAct: ActRefView | null;
  /** Later acts that only kept them in the post */
  confirmedBy: ActRefView[];
  /** Already there on the first act on file: may have taken the post before */
  openStart: boolean;
  acting: boolean;
  ended?: "replaced" | "moved" | "left" | "removed";
}

/** One post and its line through renames, with who held it */
export interface SeatView {
  key: string;
  post: string;
  label: string;
  /** 1 heads of state and government, 2 ministers, 3 secretaries general and cabinets, 4 the rest */
  tier: 1 | 2 | 3 | 4;
  /** Indexes into `tenures`, in date order: at the body as it is today */
  tenures: number[];
  /** Bodies that ended and were replaced by this one, and who held the post there */
  defunct?: DefunctBodyView[];
  /** The body this seat was at has ended, and none on the timeline took it over */
  ended?: DefunctBodyView;
  /** Set on the row of a body that ended, nested under the seat that replaced it */
  gone?: DefunctBodyView;
}

/** A body that no longer exists, as the timeline shows it */
export interface DefunctBodyView {
  label: string;
  organizationId: string | null;
  endedOn: string | null;
  how: string | null;
  replacedBy: string[];
  replacedByIds?: string[];
  tenures: number[];
}

export interface TimelineView {
  acts: GovActView[];
  tenures: TenureView[];
  /** Absent from a server older than the seats */
  seats?: SeatView[];
  /** The latest act that lists the whole government */
  latestComplete: ActRefView | null;
}

/* ------------------------------------------------------------------ */
/* Consistency checks                                                  */
/* ------------------------------------------------------------------ */

export type HealthKind =
  | "duplicate_tenure"
  | "seat_contested"
  | "two_ministries"
  | "misfiled_minister"
  | "ended_body"
  | "start_is_record_date"
  | "start_contradicted";

export interface HealthIssue {
  kind: HealthKind;
  severity: "error" | "warn";
  summary: string;
  organizationId?: string;
  stakeholderIds?: string[];
  assignmentIds?: string[];
}

export interface HealthView {
  checkedAt: string;
  issues: HealthIssue[];
}
